import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateInternalAuditMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for updateInternalAudit. Carries OriginalTimestamp for
 * optimistic concurrency; a stale timestamp surfaces as a TRPCClientError with
 * code CONFLICT. BusinessArea is free-text (resolved server-side); the legacy
 * BusinessAreaId is accepted but re-derived by the server.
 */
type UpdateInternalAuditTRPCInput = {
  Id: string;
  Title: string;
  OriginalTimestamp: string;
  Description?: string | null;
  BusinessArea?: string | null;
  BusinessAreaId?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateInternalAuditMutation => {
  return {
    updateInternalAudit: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateInternalAuditTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditEntity.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.internalAuditEntity.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
        });
        // The update find-or-creates a business_area for a new free-text title
        // and runs the org-scoped orphan cleanup server-side, so the
        // business-area list can change either way — refresh it (parity with
        // the Apollo path's evictField(cache, 'business_area')).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.businessArea.businessAreas.queryKey(),
        });
      },
    }),
  });

  return {
    updateInternalAudit: async (
      variables: UpdateInternalAuditTRPCInput
    ): Promise<UpdateInternalAuditMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
